import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ApiEndpoints } from "../api/endpoints";
import { api, getImagePath, uploadFileAnyFile } from "../utils/utils";
import { FormDataTypeBox } from "../modal/box";

// Define the shape of the video data
interface VideoState {
  videos: string[]; // Array of base64 encoded video strings
  createVideoStatus: "idle" | "loading" | "succeeded" | "failed";
  createVideoError: string | null;
  isCreateVideoInprogress: boolean;
  createAllBoxVideosStatus: "idle" | "loading" | "succeeded" | "failed";
  createAllBoxVideosError: string | null;
  isCreateAllBoxVideosInprogress: boolean;
  createAllBoxVideosSuccessMessage: string | null;

}

// Define the initial state for the video slice
const initialState: VideoState = {
  videos: [],
  createVideoStatus: "idle",
  createVideoError: null,
  isCreateVideoInprogress: false,
  createAllBoxVideosStatus: "idle",
  createAllBoxVideosError: null,
  isCreateAllBoxVideosInprogress: false,
  createAllBoxVideosSuccessMessage: null,
};

export const createVideo = createAsyncThunk<
  string, // Type for the resolved value (uploaded video URL)
  object, // Type for the argument
  { rejectValue: { message: string } } // Type for rejected value
>("videos/createVideo", async (videoData, { rejectWithValue }) => {
  try {
    // Step 1: Create the video
    const response = await api.post(ApiEndpoints.RENDER_VIDEO, videoData);

    // if (!response.data || !response.data.video_base64) {
    //   throw new Error("Video creation failed, no base64 data returned.");
    // }

    // The payload might actually be a string that needs to be parsed first
    const responseJson = JSON.parse(response.data);

    // Check if the video_base64 property exists
    if (responseJson && responseJson.video_base64) {
      // Step 2: Decode the base64 video data into a Blob
      let base64String = responseJson.video_base64;
      const base64Prefix = "data:video/webm;base64,";
      if (base64String.startsWith(base64Prefix)) {
        base64String = base64String.substring(base64Prefix.length);
      }

      // Ensure padding for base64 string
      const padding = base64String.length % 4;
      if (padding > 0) {
        base64String += "=".repeat(4 - padding);
      }

      const byteCharacters = atob(base64String);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([byteNumbers], { type: "video/webm" });
      const url = URL.createObjectURL(blob); // Create a Blob URL

      console.log(url);
      const file = new File([blob], "video.webm", { type: "video/webm" });

      // Step 3: Upload the video
      const [uploadedFileName, errorMessage] = await uploadFileAnyFile(file);

      if (!uploadedFileName) {
        throw new Error(errorMessage || "File upload failed.");
      }

      // Return the uploaded file name or URL
      return getImagePath(uploadedFileName);
    } else {
      console.error("video_base64 property not found in response:", responseJson);
    }

    return "";
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(
        error.response.data.detail || "Failed to create video"
      );
    }
    return rejectWithValue(error.message || "An error occurred");
  }
});

export const createAllBoxVideos = createAsyncThunk<
  string, // Type for the resolved value (response message or similar)
  Omit<FormDataTypeBox, 'item_1_type' | 'item_2_type' | 'item_3_type'> & { box_id: string }, // Argument type
  { rejectValue: { message: string } } // Type for rejected value
>("videos/createAllBoxVideos", async (videoData, { rejectWithValue }) => {
  try {
    // Call the API to generate box videos
    const response = await api.post(
      `${ApiEndpoints.GENERATE_BOX_VIDEOS}?box_id=${videoData.box_id}`,videoData
    );

    // Assuming the response contains a message or a success indicator
    if (response && response.data) {
      return response.data.message || "Box videos generated successfully.";
    } else {
      throw new Error("Failed to generate box videos.");
    }
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(
        error.response.data.detail || "Failed to generate box videos"
      );
    }
    return rejectWithValue(error.message || "An error occurred");
  }
});

// Create the video slice
const videoSlice = createSlice({
  name: "videos",
  initialState,
  reducers: {
    resetCreateVideoStatus: (state) => {
      state.createVideoStatus = "idle";
      state.createVideoError = null;
    },
    resetCreateAllBoxVideosStatus: (state) => {
      state.createAllBoxVideosStatus = "idle";
      state.createAllBoxVideosError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createVideo.pending, (state) => {
        state.createVideoStatus = "loading";
        state.isCreateVideoInprogress = true;
        state.createVideoError = null; // Clear previous errors
        state.videos = [];
      })
      .addCase(
        createVideo.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isCreateVideoInprogress = false;
          state.createVideoStatus = "succeeded";
      
          // Save the uploaded video URL to the state
          state.videos.push(action.payload);
          console.log("Uploaded video URL:", action.payload);
        }
      )      

      .addCase(
        createVideo.rejected,
        (state, action: PayloadAction<{ message: string } | undefined>) => {
          state.isCreateVideoInprogress = false;
          state.createVideoStatus = "failed";
          state.createVideoError =
            action.payload?.message || "Failed to create video";
        }
      )
      .addCase(createAllBoxVideos.pending, (state) => {
        state.createAllBoxVideosStatus = "loading";
        state.isCreateAllBoxVideosInprogress = true;
        state.createAllBoxVideosError = null;
      })
      .addCase(
        createAllBoxVideos.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isCreateAllBoxVideosInprogress = false;
          state.createAllBoxVideosStatus = "succeeded";
          console.log("Box videos response:", action.payload);
          state.createAllBoxVideosSuccessMessage = action.payload;
        }
      )
      .addCase(
        createAllBoxVideos.rejected,
        (state, action: PayloadAction<{ message: string } | undefined>) => {
          state.isCreateAllBoxVideosInprogress = false;
          state.createAllBoxVideosStatus = "failed";
          state.createAllBoxVideosError =
            action.payload?.message || "Failed to generate box videos";
        }
      );
  },
});

// Export actions and reducer
export const { resetCreateVideoStatus, resetCreateAllBoxVideosStatus } = videoSlice.actions;
export default videoSlice.reducer;
